import React, {
  memo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { createUseStyle } from '@redlink/assets/styles';
import {
  PreviewUserInfo,
  PinButton,
  FloatIconButton,
} from '@redlink/components/molecules';
import {
  PreviewCategoryList,
  PreviewShopPostCard,
} from '@redlink/components/muscles';
import { PreviewPostList } from '@redlink/components/organisms';
import {
  IconBasket,
  LoadingContainer,
  IconList,
  IconBoxList,
} from '@redlink/components/atoms';
import { useLocale } from '@redlink/assets/locale';
import {
  useQuery,
  getPagePreviewCategoriesQuery,
  getPagePreviewProductsQuery,
  getProductBasketItems,
} from '@redlink/services';
import { useValues } from '@redlink/assets/values';
import { useNavigation } from '@react-navigation/native';

const PAGE_SIZE = 25;
const COMPACT_MIN_SIZE = 5;

export const PreviewShopPage = memo(
  forwardRef(({ pageName }, ref) => {
    const { styles, colors } = useStyle();
    const { dictionary } = useLocale();
    const { staticData, routes } = useValues();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const currentPageIndexRef = useRef(0);
    const [fetchMoreIsBusy, setFetchMoreIsBusy] = useState(false);
    const [isCompact, setIsCompact] = useState(false);
    const flatListRef = useRef(null);
    const { navigate } = useNavigation();

    const {
      data: { CategoriesPreview = [] },
    } = useQuery(getPagePreviewCategoriesQuery, {
      variables: { pageName, type: staticData.categoryType.product },
    });

    const navigateToProductBasket = useCallback(
      () => navigate(routes.productBasket.key, { pageName }),
      [navigate, routes, pageName]
    );

    const {
      refetch,
      fetchMore,
      loading: getPagePreviewProductsIsBusy,
      data: { ProductsPreview = [] },
    } = useQuery(getPagePreviewProductsQuery, {
      variables: {
        ...(selectedCategory && { category: selectedCategory?.item?.id }),
        skip: 0,
        take: PAGE_SIZE,
        pageName,
      },
      done: ({ ProductsPreview = [] }) =>
        ProductsPreview.length > COMPACT_MIN_SIZE && setIsCompact(true),
    });

    useImperativeHandle(ref, () => ({
      refetch: () => {
        flatListRef.current.scrollToOffset({ offset: 0, animated: false });
        currentPageIndexRef.current = 0;
        refetch({
          ...(selectedCategory && { category: selectedCategory?.item?.id }),
          skip: currentPageIndexRef.current,
          take: PAGE_SIZE,
          pageName,
        });
      },
    }));

    const fetchMoreProducts = (pageIndex) => {
      setFetchMoreIsBusy(true);
      fetchMore({
        variables: {
          ...(selectedCategory && { category: selectedCategory?.item?.id }),
          skip: pageIndex * PAGE_SIZE,
          take: PAGE_SIZE,
          pageName,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          setFetchMoreIsBusy(false);
          currentPageIndexRef.current += 1;
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            ProductsPreview: [
              ...prev.ProductsPreview,
              ...fetchMoreResult.ProductsPreview,
            ],
          };
        },
      });
    };

    const {
      data: { ProductBasketItems },
    } = useQuery(getProductBasketItems);

    const basketItemsCount = ProductBasketItems.filter(
      ({ count }) => count > 0
    ).length;

    return (
      <LoadingContainer
        hasOverlay={false}
        isBusy={getPagePreviewProductsIsBusy}
        style={styles.container}
      >
        <PreviewUserInfo {...{ pageName }} style={styles.userInfoContainer} />
        <PreviewCategoryList
          data={CategoriesPreview}
          selectedIndex={selectedCategory?.index}
          style={styles.categoryListContainer}
          contentContainerStyle={styles.categoriesContainer}
          onChange={(item, index) => {
            currentPageIndexRef.current = 0;
            setSelectedCategory({ item, index });
          }}
        />
        <PreviewPostList
          ref={flatListRef}
          data={ProductsPreview}
          ItemComponent={({ item, ...rest }) => (
            <PreviewShopPostCard
              {...rest}
              {...{ item }}
              compact={isCompact}
              onPress={() => {
                navigate(routes.previewPostDetail.key, {
                  pageName,
                  shortLink: item.shortLink,
                });
              }}
            />
          )}
          onEndReachedThreshold={0.5}
          onEndReached={() =>
            fetchMoreProducts(currentPageIndexRef.current + 1)
          }
        />
        {basketItemsCount > 0 && (
          <PinButton
            title={`${basketItemsCount} ${dictionary.cases} - ${dictionary.reviewShoppingList}`}
            icon={<IconBasket />}
            onPress={navigateToProductBasket}
          />
        )}
        <FloatIconButton onPress={() => setIsCompact((prev) => !prev)}>
          {isCompact ? (
            <IconBoxList color={colors.cardBackground} />
          ) : (
            <IconList color={colors.cardBackground} />
          )}
        </FloatIconButton>
      </LoadingContainer>
    );
  })
);

const useStyle = createUseStyle(({ colors, size, shadow }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  userInfoContainer: {
    margin: 24,
  },
  categoryListContainer: {},
  categoriesContainer: {
    paddingHorizontal: 24,
  },
}));
